//import axios, { endpoints } from 'src/lib/axios';
import axios from 'axios';
import { isEmpty } from 'lodash';
import { useAuth0 } from '@auth0/auth0-react';
import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { singleFlight } from 'src/utils/network-call';

import { endpoints } from 'src/lib/axios';
import { getConfig } from 'src/global-config';
import { handleSessionLogout } from 'src/observability/datadog-helpers';
import { setAxiosToken, registerHRTokenRefresher } from 'src/lib/axios-store';

import { toast } from 'src/components/snackbar';
import { SplashScreen } from 'src/components/loading-screen';

import { useStoreLocalStorage } from 'src/auth/hooks/use-store-localStorage';

import { HRAuthContext } from '.';

export const HRAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const CONFIG = getConfig();

  const [loadingHRAuth, setLoadingHRAuth] = useState<boolean>(true);
  const [hrAuthData, setHrAuthData] = useState<any>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const { isAuthenticated, getAccessTokenSilently, logout } = useAuth0();

  const { saveValue: saveCustomToken } = useStoreLocalStorage('customToken', '');
  const { saveValue: saveCustomRefreshToken } = useStoreLocalStorage('customRefreshToken', '');
  const { saveValue: saveCustomTokenExpiry } = useStoreLocalStorage<number|string>('customTokenExpiry', 0);
  const { getValue: getCustomToken } = useStoreLocalStorage('customToken', '');
  const { saveValue: setHRContext } = useStoreLocalStorage('hrContext', '');
  const { getValue: getHRContext } = useStoreLocalStorage('hrContext', '');

  const { method } = CONFIG.auth;

  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  const MAX_TIMEOUT_MS = 2147483647;

  const clearRefreshTimeout = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleRefresh = (issuedAt: number, expiresIn: number) => {
    const now = Math.floor(Date.now() / 1000);
    const refreshInSeconds = issuedAt + expiresIn - now - 120;
    const refreshInMs = Math.max(refreshInSeconds * 1000, 0);

    if(isNaN(refreshInMs) || refreshInMs <= 0){
      console.warn("Invalid time passed to refreshing the token");
      return;
    }

    // Cap the timeout at JavaScript's maximum to prevent immediate firing
    const cappedRefreshInMs = Math.min(refreshInMs, MAX_TIMEOUT_MS);
    
    if (cappedRefreshInMs < refreshInMs) {
      console.warn(`Token refresh time (${refreshInMs}ms) exceeds max setTimeout. Capping at ${cappedRefreshInMs}ms (${Math.floor(cappedRefreshInMs / 1000 / 60 / 60 / 24)} days)`);
    }
  
    clearRefreshTimeout();
    refreshTimerRef.current = setTimeout(() => {
      if(isAuthenticated){
        safeExchangeToken();
      }
    }, cappedRefreshInMs);
  };

  const handleLogout = async () => {
    try{
      //destroyProductFruits();
      logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      });

      handleSessionLogout();
      saveCustomToken('');
      setAxiosToken(null);
      saveCustomRefreshToken('');
      saveCustomTokenExpiry('');
      setHRContext('');
      clearRefreshTimeout();
    }catch(error){
      console.error(error);
    }
  };

  const exchangeToken = useCallback(async () => {
    const auth0Token = await getAccessTokenSilently();
   
    setLoadingHRAuth(true);
    try {
      const response = await axios.get(CONFIG.develop.baseUrl + endpoints.auth0.exchangeToken, {
        headers: {
          Authorization: `Bearer ${auth0Token}`,
        },
      });

      const {
        Token,
        ExpiresIn,
        IsUserOnboarded,
        EmailConfirmed,
        OrganizationId,
        OrganizationName,
        OrganizationSlug,
        UserId,
        DisplayName,
        FirstName,
        LastName,
        Email,
        Auth0UserId,
        IssuedAt
      } = response.data;

      if(refreshTimerRef.current){
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      
      setHRContext(response.data);
      saveCustomToken(Token);
      saveCustomRefreshToken(Token);
      saveCustomTokenExpiry(ExpiresIn);
      setAxiosToken(Token);
      
      // Store tokens & info in state
      setHrAuthData({
        Token,
        ExpiresIn,
        IsUserOnboarded,
        EmailConfirmed,
        OrganizationId,
        OrganizationName,
        OrganizationSlug,
        Auth0UserId,
        UserId,
        DisplayName,
        FirstName,
        LastName,
        Email,
      });

      scheduleRefresh(IssuedAt, ExpiresIn);

      setLoadingHRAuth(false);
      return response.data;
    } catch (err) {
      console.error('HR token exchange failed:', err);
      setHrAuthData(null);
      setAxiosToken(null);
      setLoadingHRAuth(false);
      toast.error('Failed', 
        { 
          description: 'HR token exchange failed. Please sign in again after sometime.'
        }
      );
      setTimeout(() => {
        handleLogout();
      }, 2000);
      return null;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  const safeExchangeToken = useMemo(
    () => singleFlight(exchangeToken),
    [exchangeToken]
  );

  useEffect(() => {
    const initializeFromStorage = () => {
      const existingToken = getCustomToken();
      const existingContext = getHRContext() as any;

      if (!isEmpty(existingToken) && !isEmpty(existingContext)) {
        const issuedAt = Number(existingContext?.IssuedAt);
        const expiresIn = Number(existingContext?.ExpiresIn);
        const now = Math.floor(Date.now() / 1000); // current time in seconds

        if (!issuedAt || !expiresIn) {
          console.warn('Missing issuedAt or expiresIn in localStorage. Skipping initialization.');
          return;
        }

        const isValidToken = issuedAt + expiresIn - 120 > now;

        if (isValidToken) {
          setHrAuthData(existingContext);
          setAxiosToken(existingToken);
          // if (existingToken) {
          //   axios.defaults.headers.common.Authorization = `Bearer ${existingToken}`;
          // }
          scheduleRefresh(issuedAt, expiresIn);
          setLoadingHRAuth(false);
        } else {
          safeExchangeToken();
        }

        // We have existing data, use it
        // setHrAuthData(existingContext);
        //   if (existingToken) {
        //     axios.defaults.headers.common.Authorization = `Bearer ${existingToken}`;
        //   }
      } else if (isAuthenticated && isEmpty(existingToken)) {
        // We need to exchange token
        safeExchangeToken();
      } else {
        // No auth data available
        setLoadingHRAuth(false);
      }

      setIsInitialized(true);
    };

    initializeFromStorage();
  }, [safeExchangeToken]);

  // Clear interval when user logs out or when there's no auth0AccessToken
  useEffect(() => {
    if (!isAuthenticated) {
      clearRefreshTimeout();
      setHrAuthData(null);
      setAxiosToken(null);
    }
  }, [isAuthenticated, clearRefreshTimeout]);

  useEffect(
    () => () => {
      clearRefreshTimeout();
    },
    [clearRefreshTimeout]
  );

  useEffect(() => {
    registerHRTokenRefresher(safeExchangeToken);
  }, [safeExchangeToken]);

  // Seed the user's timezone from the browser, once per session, after the session is established.
  //
  // The activity report and the daily digest both hold back a follower who has no stored timezone
  // rather than mail them on UTC hours — which, before this, meant they reached only people who had
  // opened Account Settings → Notifications and pressed Save. Following a job was meant to be the
  // subscription; without a zone it was not enough.
  //
  // Write-once on the server, so a zone the user chose always wins and signing in from a laptop in
  // another region never moves their delivery hour. Best-effort and deliberately silent: nobody asked
  // for this on their way in, so it must not block them or surface an error.
  //
  // The ref is what makes "once" true. The token has to stay in the deps — isAuthenticated flips
  // BEFORE exchangeToken sets hrAuthData, so an effect watching authentication alone would run with no
  // token and never run again — but every silent refresh replaces that token for the life of the
  // session, and each replacement would otherwise re-fire a PUT writing a value that cannot change
  // after the first success. StrictMode is the sharper edge: it double-invokes the effect on mount, so
  // without the ref a brand-new user's first sign-in sends two CONCURRENT inserts for a row that has a
  // unique index on UserId. Set before the request, not in its callback, so the second invocation is
  // already blocked when it runs.
  const timezoneSeededRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      // A sign-out ends the session; a later sign-in on the same page is a new one and may be a
      // different user.
      timezoneSeededRef.current = false;
      return;
    }

    if (timezoneSeededRef.current || !hrAuthData?.Token) {
      return;
    }

    let zone = '';
    try {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
    } catch {
      zone = '';
    }

    if (!zone) {
      return;
    }

    timezoneSeededRef.current = true;

    axios
      .put(
        CONFIG.develop.baseUrl + endpoints.account.notificationTimezone,
        { Timezone: zone },
        { headers: { Authorization: `Bearer ${hrAuthData.Token}` } }
      )
      .catch(() => {
        /* best-effort: the user stays held back and the next sign-in tries again */
      });
  }, [isAuthenticated, hrAuthData?.Token]);

  const HRContextMemoized = useMemo(() => {
    if (!isInitialized) {
      return undefined;
    }

    // Get the stored context once to avoid multiple localStorage reads
    const storedContext = getHRContext() as any;

    return {
      loadingHRAuth,
      Token: hrAuthData?.Token,
      ExpiresIn: hrAuthData?.ExpiresIn || storedContext?.ExpiresIn,
      IssuedAt: hrAuthData?.IssuedAt || storedContext?.IssuedAt,
      IsUserOnboarded: hrAuthData?.IsUserOnboarded || storedContext?.IsUserOnboarded,
      EmailConfirmed: hrAuthData?.EmailConfirmed || storedContext?.EmailConfirmed,
      Auth0UserId: hrAuthData?.Auth0UserId || storedContext?.Auth0UserId,
      OrganizationId: hrAuthData?.OrganizationId || storedContext?.OrganizationId,
      OrganizationName: hrAuthData?.OrganizationName || storedContext?.OrganizationName,
      OrganizationSlug: hrAuthData?.OrganizationSlug || storedContext?.OrganizationSlug,
      UserId: hrAuthData?.UserId || storedContext?.UserId,
      DisplayName: hrAuthData?.DisplayName || storedContext?.DisplayName,
      FirstName: hrAuthData?.FirstName || storedContext?.FirstName,
      LastName: hrAuthData?.LastName || storedContext?.LastName,
      Email: hrAuthData?.Email || storedContext?.Email,
      exchangeToken,
      clearRefreshTimeout,
      setHrAuthData,
    };
  }, [isInitialized, loadingHRAuth, hrAuthData, exchangeToken, clearRefreshTimeout]);

  if (!isInitialized) {
    return <SplashScreen />;
  }

  return <HRAuthContext.Provider value={HRContextMemoized}>{children}</HRAuthContext.Provider>;
};
